import { useQuery } from '@tanstack/react-query';

import { bamsShortsApi } from 'api/bamsShorts';

import { BAMS_SHORTS_GC_TIME, BAMS_SHORTS_QUERY_KEY, BAMS_SHORTS_STALE_TIME } from './bamsShortsQueryKeys';

/**
 * Live on-hand inventory for a set of shorted SKUs. Dependent on the SKU-detail result,
 * so it stays disabled until there is at least one SKU to look up.
 *
 * A failed API status throws so the chase list surfaces a load error instead of
 * rendering empty inventory (which reads as "no stock"). Returns the raw API rows.
 *
 * @param {Object} params
 * @param {Array<number|string>} params.skus sorted, de-duplicated external SKUs
 * @param {boolean} [params.enabled=true]
 * @returns {import('@tanstack/react-query').UseQueryResult<Array<Object>, Error>}
 */
export const useBamsShortsInventoryQuery = ({ enabled = true, skus }) =>
    useQuery({
        queryKey: [BAMS_SHORTS_QUERY_KEY, 'inventory', skus],
        enabled: enabled && Array.isArray(skus) && skus.length > 0,
        staleTime: BAMS_SHORTS_STALE_TIME,
        gcTime: BAMS_SHORTS_GC_TIME,
        queryFn: async () => {
            const resp = await bamsShortsApi.getInventory(skus);
            if (resp?.status === false) {
                throw new Error(resp?.msg || 'Could not load live inventory.');
            }
            return resp?.data?.rows || [];
        },
    });
